import React from 'react';
import { Box, Skeleton } from '@mui/material';
import StoryTimelineBar from './StoryTimelineBar';
import { prepareLivestreamTimelineChartData } from './livestreamTimelineChartData';

export interface LivestreamBrandMention {
    brandName: string;
    mentionStartTime: number;
    mentionType?: 'paid' | 'organic' | 'neutral';
    s3Key?: string;
    logoUrl?: string;
}

interface Snapshot {
    _id: string;
    timestamp: string;
    concurrentViewers: number;
    likeCount: number;
}

interface LivestreamById {
    averageViewers?: number | null;
    peakViewers?: number | null;
    actualStartTime?: string;
    releaseTime?: string;
    youtubeDuration?: number;
    chatMessageCount?: number | null;
    chatUniqueUsers?: number | null;
    finalChatUniqueUsers?: number | null;
}

interface LivestreamTimelineBlockProps {
    videoId?: string;
    livestreamById?: LivestreamById;
    timelineSnapshots?: Snapshot[];
    timelineTotalDuration?: number;
    brandMentions?: LivestreamBrandMention[];
    includeChatStats?: boolean;
    /** When true and timeline data is not yet ready, reserve layout with a StoryTimelineBar-shaped skeleton. */
    isLoading?: boolean;
}

function LivestreamTimelineSkeleton() {
    return (
        <Box sx={{ mb: 3 }}>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1,
                }}
            >
                <Skeleton variant="text" width={80} height={20} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Skeleton variant="text" width={72} height={16} />
                    <Skeleton variant="circular" width={28} height={28} />
                </Box>
            </Box>
            <Box
                sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                }}
            >
                <Skeleton
                    variant="rounded"
                    height={160}
                    sx={{ borderRadius: 3 }}
                />
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr 1fr',
                            sm: '1fr 1fr',
                            md: '1fr 1fr 1fr 1fr',
                        },
                        gap: 2,
                        mt: 2,
                    }}
                >
                    {Array.from({ length: 4 }).map((_, i) => (
                        <Skeleton
                            key={i}
                            variant="rounded"
                            height={56}
                            sx={{ borderRadius: 2 }}
                        />
                    ))}
                </Box>
            </Box>
        </Box>
    );
}

export const LivestreamTimelineBlock: React.FC<LivestreamTimelineBlockProps> = ({
    videoId,
    livestreamById,
    timelineSnapshots = [],
    timelineTotalDuration = 0,
    brandMentions = [],
    includeChatStats = true,
    isLoading = false,
}) => {
    const avgViewers = livestreamById?.averageViewers ?? null;
    const peakViewers = livestreamById?.peakViewers ?? null;
    const chatMessageCount = includeChatStats ? (livestreamById?.chatMessageCount ?? null) : null;
    const uniqueChatUsers = includeChatStats ? (livestreamById?.chatUniqueUsers ?? livestreamById?.finalChatUniqueUsers ?? null) : null;

    const { hasTimelineData, displayStartTime, displayDuration, minuteSnapshots } =
        React.useMemo(
            () =>
                prepareLivestreamTimelineChartData({
                    timelineSnapshots,
                    timelineTotalDuration,
                    livestreamById: livestreamById ?? {},
                }),
            [timelineSnapshots, timelineTotalDuration, livestreamById],
        );

    const mappedMentions = brandMentions.map((m) => ({
        brandName: m.brandName,
        mentionStartTime: m.mentionStartTime,
        mentionType: (m.mentionType ?? 'neutral') as 'paid' | 'organic' | 'neutral',
        s3Key: m.s3Key,
        logoUrl: m.logoUrl,
    }));

    if (isLoading && !hasTimelineData) {
        return <LivestreamTimelineSkeleton />;
    }

    if (!hasTimelineData) {
        return null;
    }

    return (
        <Box>
            <StoryTimelineBar
                averageViewers={avgViewers}
                peakViewers={peakViewers}
                chatMessageCount={chatMessageCount}
                uniqueChatUsers={uniqueChatUsers}
                totalDuration={Math.round(displayDuration)}
                brandMentions={mappedMentions}
                videoId={videoId}
                snapshots={minuteSnapshots}
                actualStartTime={displayStartTime}
            />
        </Box>
    );
};
